/**
 * SUP-ALIGN-1 — Supply-side schema lock.
 * Defines Zod schemas for all 21 KIOL supply-side Firestore collections.
 * These schemas are the authoritative contract for the KIOL layer.
 *
 * Collections locked:
 *   kiol_module_state, ims_a_inventory, ims_b_inventory, ims_c_inventory,
 *   ims_d_inventory, ims_locations, ims_lot_records, kiol_forecasts,
 *   demand_sensing_signals, kiol_demand_signals, meio_policies, meio_runs,
 *   meio_optimization_results, supplier_inventory_feeds, supplier_risk_signals,
 *   supplier_tier_graph, po_recommendations, kiol_pooling_agreements,
 *   kiol_events, supply_schema_versions, supply_migrations
 */
import { z } from 'zod';
import { TenantIdSchema, UlidSchema, IsoDateSchema, MoneySchema } from './_primitives.js';

// ── Shared supply primitives ──────────────────────────────────────────────────

export const QuantitySchema = z.object({
  value:    z.number().nonnegative(),
  unit:     z.string().min(1).max(32),
});

export const LocationNodeTypeSchema = z.enum([
  'dc', 'branch', '3pl', 'supplier', 'customer', 'transit',
]);

export const ImsSubstrateSchema = z.enum(['ims_a', 'ims_b', 'ims_c', 'ims_d']);

export const ForecastModelSchema = z.enum([
  'arima', 'exponential_smoothing', 'gradient_boosting', 'rnn', 'lstm', 'ensemble',
]);

export const RiskLevelSchema = z.enum(['low', 'medium', 'high', 'critical']);

export const SupplySchemaVersion = '1.0.0';

// ── 1. kiol_module_state ──────────────────────────────────────────────────────

export const KIOL_MODULE_STATUS = Object.freeze({
  ACTIVATING: 'activating',
  ACTIVE:     'active',
  SUSPENDED:  'suspended',
  INACTIVE:   'inactive',
});

export const KiolModuleStateSchema = z.object({
  tenantId:        TenantIdSchema,
  status:          z.enum(Object.values(KIOL_MODULE_STATUS)),
  activatedAt:     IsoDateSchema.nullable(),
  activatedBy:     z.string().min(1).nullable(),
  schemaVersion:   z.string().default(SupplySchemaVersion),
  imsSubstrates:   z.array(ImsSubstrateSchema),
  capabilities:    z.array(z.string()),
  softDeleted:     z.boolean().default(false),
  updatedAt:       IsoDateSchema,
  createdAt:       IsoDateSchema,
});

// ── 2. ims_a_inventory (raw materials) ───────────────────────────────────────

export const ImsAInventorySchema = z.object({
  tenantId:       TenantIdSchema,
  itemId:         UlidSchema,
  skuCode:        z.string().min(1).max(128),
  description:    z.string().max(512),
  locationNodeId: UlidSchema,
  onHand:         QuantitySchema,
  reserved:       QuantitySchema,
  committed:      QuantitySchema,
  reorderPoint:   QuantitySchema.nullable(),
  reorderQty:     QuantitySchema.nullable(),
  supplierId:     UlidSchema.nullable(),
  lotId:          UlidSchema.nullable(),
  valuationUnit:  MoneySchema.nullable(),
  softDeleted:    z.boolean().default(false),
  updatedAt:      IsoDateSchema,
  createdAt:      IsoDateSchema,
});

// ── 3. ims_b_inventory (WIP / in-transit) ────────────────────────────────────

export const TRANSIT_STATUS = Object.freeze({
  PENDING:    'pending',
  IN_TRANSIT: 'in_transit',
  ARRIVED:    'arrived',
  DELAYED:    'delayed',
  EXCEPTION:  'exception',
});

export const ImsBInventorySchema = z.object({
  tenantId:         TenantIdSchema,
  itemId:           UlidSchema,
  skuCode:          z.string().min(1).max(128),
  transitStatus:    z.enum(Object.values(TRANSIT_STATUS)),
  originNodeId:     UlidSchema,
  destinationNodeId:UlidSchema,
  quantity:         QuantitySchema,
  expectedArrival:  IsoDateSchema.nullable(),
  actualArrival:    IsoDateSchema.nullable(),
  carrierId:        UlidSchema.nullable(),
  loadId:           UlidSchema.nullable(),
  lotId:            UlidSchema.nullable(),
  softDeleted:      z.boolean().default(false),
  updatedAt:        IsoDateSchema,
  createdAt:        IsoDateSchema,
});

// ── 4. ims_c_inventory (finished goods) ──────────────────────────────────────

export const ImsCInventorySchema = z.object({
  tenantId:       TenantIdSchema,
  itemId:         UlidSchema,
  skuCode:        z.string().min(1).max(128),
  locationNodeId: UlidSchema,
  onHand:         QuantitySchema,
  reserved:       QuantitySchema,
  committed:      QuantitySchema,
  available:      QuantitySchema,
  expiryDate:     IsoDateSchema.nullable(),
  lotId:          UlidSchema.nullable(),
  warehouseZone:  z.string().max(64).nullable(),
  valuationUnit:  MoneySchema.nullable(),
  softDeleted:    z.boolean().default(false),
  updatedAt:      IsoDateSchema,
  createdAt:      IsoDateSchema,
});

// ── 5. ims_d_inventory (specialty materials) ─────────────────────────────────

export const SPECIALTY_CLASS = Object.freeze({
  HAZMAT:       'hazmat',
  PERISHABLE:   'perishable',
  CONTROLLED:   'controlled',
  BONDED:       'bonded',
  REFRIGERATED: 'refrigerated',
  FROZEN:       'frozen',
  OTHER:        'other',
});

export const ImsDInventorySchema = z.object({
  tenantId:        TenantIdSchema,
  itemId:          UlidSchema,
  skuCode:         z.string().min(1).max(128),
  specialtyClass:  z.enum(Object.values(SPECIALTY_CLASS)),
  locationNodeId:  UlidSchema,
  quantity:        QuantitySchema,
  complianceCodes: z.array(z.string().max(64)).default([]),
  handlingNotes:   z.string().max(1024).nullable(),
  lotId:           UlidSchema.nullable(),
  expiryDate:      IsoDateSchema.nullable(),
  softDeleted:     z.boolean().default(false),
  updatedAt:       IsoDateSchema,
  createdAt:       IsoDateSchema,
});

// ── 6. ims_locations ──────────────────────────────────────────────────────────

export const ImsLocationSchema = z.object({
  tenantId:     TenantIdSchema,
  nodeId:       UlidSchema,
  displayName:  z.string().min(1).max(256),
  nodeType:     LocationNodeTypeSchema,
  parentNodeId: UlidSchema.nullable(),
  address:      z.object({
    line1:   z.string().max(256),
    city:    z.string().max(128),
    state:   z.string().max(64),
    country: z.string().length(2),
    zip:     z.string().max(20),
  }).nullable(),
  coordinates:  z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  }).nullable(),
  capabilities: z.array(z.string()).default([]),
  active:       z.boolean().default(true),
  softDeleted:  z.boolean().default(false),
  updatedAt:    IsoDateSchema,
  createdAt:    IsoDateSchema,
});

// ── 7. ims_lot_records ────────────────────────────────────────────────────────

export const ImsLotRecordSchema = z.object({
  tenantId:       TenantIdSchema,
  lotId:          UlidSchema,
  lotNumber:      z.string().min(1).max(128),
  itemId:         UlidSchema,
  substrate:      ImsSubstrateSchema,
  quantity:       QuantitySchema,
  manufacturedAt: IsoDateSchema.nullable(),
  expiresAt:      IsoDateSchema.nullable(),
  supplierId:     UlidSchema.nullable(),
  certifications: z.array(z.string().max(64)).default([]),
  softDeleted:    z.boolean().default(false),
  updatedAt:      IsoDateSchema,
  createdAt:      IsoDateSchema,
});

// ── 8. kiol_forecasts ─────────────────────────────────────────────────────────

export const FORECAST_HORIZON = Object.freeze({
  DAILY:    'daily',
  WEEKLY:   'weekly',
  MONTHLY:  'monthly',
  QUARTERLY:'quarterly',
});

export const FORECAST_STATUS = Object.freeze({
  PENDING:   'pending',
  COMPLETE:  'complete',
  FAILED:    'failed',
  STALE:     'stale',
});

export const KiolForecastSchema = z.object({
  tenantId:        TenantIdSchema,
  forecastId:      UlidSchema,
  itemId:          UlidSchema,
  locationNodeId:  UlidSchema.nullable(),
  horizon:         z.enum(Object.values(FORECAST_HORIZON)),
  model:           ForecastModelSchema,
  status:          z.enum(Object.values(FORECAST_STATUS)),
  periodStart:     IsoDateSchema,
  periodEnd:       IsoDateSchema,
  forecastedQty:   z.number(),
  confidenceLow:   z.number().nullable(),
  confidenceHigh:  z.number().nullable(),
  mape:            z.number().min(0).max(1).nullable(),
  ensembleWeights: z.record(z.string(), z.number()).nullable(),
  calibrationKey:  z.string().nullable(),
  generatedAt:     IsoDateSchema,
  softDeleted:     z.boolean().default(false),
  createdAt:       IsoDateSchema,
});

// ── 9. demand_sensing_signals ─────────────────────────────────────────────────

export const SIGNAL_TYPE = Object.freeze({
  ORDER_VELOCITY:   'order_velocity',
  PRICE_CHANGE:     'price_change',
  SEASONAL_SPIKE:   'seasonal_spike',
  COMPETITOR_EVENT: 'competitor_event',
  WEATHER:          'weather',
  CUSTOM:           'custom',
});

export const DemandSensingSignalSchema = z.object({
  tenantId:    TenantIdSchema,
  signalId:    UlidSchema,
  signalType:  z.enum(Object.values(SIGNAL_TYPE)),
  itemId:      UlidSchema.nullable(),
  magnitude:   z.number(),
  direction:   z.enum(['up', 'down', 'neutral']),
  confidence:  z.number().min(0).max(1),
  source:      z.string().max(128),
  detectedAt:  IsoDateSchema,
  expiresAt:   IsoDateSchema.nullable(),
  metadata:    z.record(z.string(), z.unknown()).default({}),
  softDeleted: z.boolean().default(false),
  createdAt:   IsoDateSchema,
});

// ── 10. kiol_demand_signals ───────────────────────────────────────────────────

export const KiolDemandSignalSchema = z.object({
  tenantId:          TenantIdSchema,
  aggregationId:     UlidSchema,
  periodStart:       IsoDateSchema,
  periodEnd:         IsoDateSchema,
  itemId:            UlidSchema.nullable(),
  locationNodeId:    UlidSchema.nullable(),
  rawSignalCount:    z.int().nonnegative(),
  aggregatedMagnitude: z.number(),
  falsePositiveFlags: z.int().nonnegative().default(0),
  aegisPatternKey:   z.string().nullable(),
  softDeleted:       z.boolean().default(false),
  createdAt:         IsoDateSchema,
});

// ── 11. meio_policies ─────────────────────────────────────────────────────────

export const MEIO_SOLVER_TYPE = Object.freeze({
  NEWSVENDOR:     'newsvendor',
  ECHELON_STOCK:  'echelon_stock',
  BASE_STOCK:     'base_stock',
  REORDER_POINT:  'reorder_point',
});

export const MeioPolicySchema = z.object({
  tenantId:        TenantIdSchema,
  policyId:        UlidSchema,
  displayName:     z.string().min(1).max(256),
  solverType:      z.enum(Object.values(MEIO_SOLVER_TYPE)),
  locationNodeIds: z.array(UlidSchema).min(1),
  itemScope:       z.enum(['all', 'category', 'explicit']),
  itemIds:         z.array(UlidSchema).default([]),
  itemCategories:  z.array(z.string().max(64)).default([]),
  serviceLevel:    z.number().min(0).max(1),
  leadTimeDays:    z.number().positive(),
  reviewPeriodDays:z.number().positive(),
  active:          z.boolean().default(true),
  softDeleted:     z.boolean().default(false),
  updatedAt:       IsoDateSchema,
  createdAt:       IsoDateSchema,
});

// ── 12. meio_runs ─────────────────────────────────────────────────────────────

export const MEIO_RUN_STATUS = Object.freeze({
  QUEUED:    'queued',
  RUNNING:   'running',
  COMPLETE:  'complete',
  FAILED:    'failed',
  INFEASIBLE:'infeasible',
});

export const MeioRunSchema = z.object({
  tenantId:        TenantIdSchema,
  runId:           UlidSchema,
  policyId:        UlidSchema,
  status:          z.enum(Object.values(MEIO_RUN_STATUS)),
  triggeredBy:     z.enum(['schedule', 'manual', 'event']),
  triggeredAt:     IsoDateSchema,
  completedAt:     IsoDateSchema.nullable(),
  durationMs:      z.int().nonnegative().nullable(),
  nodesOptimized:  z.int().nonnegative().default(0),
  iterationsRun:   z.int().nonnegative().default(0),
  converged:       z.boolean().nullable(),
  errorMessage:    z.string().max(1024).nullable(),
  softDeleted:     z.boolean().default(false),
  createdAt:       IsoDateSchema,
});

// ── 13. meio_optimization_results ────────────────────────────────────────────

export const MeioOptimizationResultSchema = z.object({
  tenantId:        TenantIdSchema,
  resultId:        UlidSchema,
  runId:           UlidSchema,
  locationNodeId:  UlidSchema,
  itemId:          UlidSchema,
  recommendedSafetyStock: QuantitySchema,
  recommendedReorderPoint:QuantitySchema,
  recommendedOrderQty:    QuantitySchema,
  projectedServiceLevel:  z.number().min(0).max(1),
  estimatedHoldingCost:   MoneySchema.nullable(),
  appliedAt:       IsoDateSchema.nullable(),
  softDeleted:     z.boolean().default(false),
  createdAt:       IsoDateSchema,
});

// ── 14. supplier_inventory_feeds ──────────────────────────────────────────────

export const SupplierInventoryFeedSchema = z.object({
  tenantId:      TenantIdSchema,
  feedId:        UlidSchema,
  supplierId:    UlidSchema,
  itemId:        UlidSchema,
  supplierSku:   z.string().max(128),
  availableQty:  QuantitySchema,
  leadTimeDays:  z.number().nonnegative(),
  priceUnit:     MoneySchema.nullable(),
  feedTimestamp: IsoDateSchema,
  source:        z.enum(['edi', 'api', 'manual', 'portal']),
  softDeleted:   z.boolean().default(false),
  createdAt:     IsoDateSchema,
});

// ── 15. supplier_risk_signals ─────────────────────────────────────────────────

export const SupplierRiskSignalSchema = z.object({
  tenantId:    TenantIdSchema,
  signalId:    UlidSchema,
  supplierId:  UlidSchema,
  riskLevel:   RiskLevelSchema,
  riskCategory:z.enum(['financial', 'operational', 'geopolitical', 'quality', 'compliance', 'capacity']),
  description: z.string().max(2048),
  source:      z.string().max(256),
  detectedAt:  IsoDateSchema,
  resolvedAt:  IsoDateSchema.nullable(),
  falsePositive: z.boolean().default(false),
  metadata:    z.record(z.string(), z.unknown()).default({}),
  softDeleted: z.boolean().default(false),
  createdAt:   IsoDateSchema,
});

// ── 16. supplier_tier_graph ───────────────────────────────────────────────────

export const SUPPLIER_TIER = Object.freeze({
  PRIMARY:   'primary',    // T1 — direct supplier
  SECONDARY: 'secondary',  // T2 — supplier's supplier
  TERTIARY:  'tertiary',   // T3 — further upstream
});

export const SupplierTierGraphSchema = z.object({
  tenantId:       TenantIdSchema,
  edgeId:         UlidSchema,
  fromSupplierId: UlidSchema,
  toSupplierId:   UlidSchema.nullable(),
  tier:           z.enum(Object.values(SUPPLIER_TIER)),
  itemCategories: z.array(z.string().max(64)).default([]),
  shareOfWallet:  z.number().min(0).max(1).nullable(),
  riskScore:      z.number().min(0).max(100).nullable(),
  active:         z.boolean().default(true),
  softDeleted:    z.boolean().default(false),
  updatedAt:      IsoDateSchema,
  createdAt:      IsoDateSchema,
});

// ── 17. po_recommendations ────────────────────────────────────────────────────

export const PO_RECOMMENDATION_STATUS = Object.freeze({
  PENDING:   'pending',
  APPROVED:  'approved',
  REJECTED:  'rejected',
  AUTO_SENT: 'auto_sent',
  EXPIRED:   'expired',
});

export const PoRecommendationSchema = z.object({
  tenantId:      TenantIdSchema,
  recommendationId: UlidSchema,
  supplierId:    UlidSchema,
  itemId:        UlidSchema,
  locationNodeId:UlidSchema,
  status:        z.enum(Object.values(PO_RECOMMENDATION_STATUS)),
  recommendedQty:QuantitySchema,
  estimatedCost: MoneySchema.nullable(),
  triggerReason: z.enum(['reorder_point', 'meio_output', 'demand_spike', 'manual']),
  autoPoThreshold:z.boolean().default(false),
  approvedBy:    z.string().nullable(),
  approvedAt:    IsoDateSchema.nullable(),
  expiresAt:     IsoDateSchema.nullable(),
  softDeleted:   z.boolean().default(false),
  updatedAt:     IsoDateSchema,
  createdAt:     IsoDateSchema,
});

// ── 18. kiol_pooling_agreements ───────────────────────────────────────────────
// Future: cross-tenant inventory pooling (opt-in only; foundation purity guaranteed).

export const POOLING_STATUS = Object.freeze({
  DRAFT:     'draft',
  ACTIVE:    'active',
  SUSPENDED: 'suspended',
  TERMINATED:'terminated',
});

export const KiolPoolingAgreementSchema = z.object({
  tenantId:          TenantIdSchema,         // owning tenant (initiator)
  agreementId:       UlidSchema,
  partnerTenantId:   TenantIdSchema,         // explicit opt-in required
  status:            z.enum(Object.values(POOLING_STATUS)),
  itemCategories:    z.array(z.string().max(64)).min(1),
  allocationPolicy:  z.enum(['proportional', 'priority', 'negotiated']),
  expiresAt:         IsoDateSchema.nullable(),
  operatorConsentId: z.string().min(1),      // explicit operator consent record
  softDeleted:       z.boolean().default(false),
  updatedAt:         IsoDateSchema,
  createdAt:         IsoDateSchema,
});

// ── 19. kiol_events ───────────────────────────────────────────────────────────
// EventBus delivery via Firestore per EVENTBUS CONTRACT DOCTRINE.

export const KIOL_EVENT_STATUS = Object.freeze({
  PENDING:   'pending',
  DELIVERED: 'delivered',
  FAILED:    'failed',
});

export const KiolEventSchema = z.object({
  tenantId:    TenantIdSchema,
  eventId:     UlidSchema,
  topic:       z.string().min(1).max(128),
  payload:     z.record(z.string(), z.unknown()),
  status:      z.enum(Object.values(KIOL_EVENT_STATUS)),
  emittedAt:   IsoDateSchema,
  deliveredAt: IsoDateSchema.nullable(),
  retryCount:  z.int().nonnegative().default(0),
  softDeleted: z.boolean().default(false),
  createdAt:   IsoDateSchema,
});

// ── 20. supply_schema_versions ────────────────────────────────────────────────

export const SupplySchemaVersionRecordSchema = z.object({
  tenantId:      TenantIdSchema,
  collection:    z.string().min(1).max(128),
  version:       z.string().min(1).max(32),
  lockedAt:      IsoDateSchema,
  lockedBy:      z.string().min(1),
  changeNotes:   z.string().max(2048).nullable(),
  softDeleted:   z.boolean().default(false),
  createdAt:     IsoDateSchema,
});

// ── 21. supply_migrations ─────────────────────────────────────────────────────

export const MIGRATION_STATUS = Object.freeze({
  PENDING:   'pending',
  RUNNING:   'running',
  COMPLETE:  'complete',
  FAILED:    'failed',
  ROLLED_BACK:'rolled_back',
});

export const SupplyMigrationSchema = z.object({
  tenantId:    TenantIdSchema,
  migrationId: UlidSchema,
  scriptName:  z.string().min(1).max(256),
  status:      z.enum(Object.values(MIGRATION_STATUS)),
  startedAt:   IsoDateSchema,
  completedAt: IsoDateSchema.nullable(),
  errorMessage:z.string().max(4096).nullable(),
  collectionsAffected: z.array(z.string().max(128)).default([]),
  softDeleted: z.boolean().default(false),
  createdAt:   IsoDateSchema,
});

// ── SUPPLY_SCHEMA_REGISTRY (all 21 collections) ───────────────────────────────

export const SUPPLY_SCHEMA_REGISTRY = Object.freeze({
  kiol_module_state:         KiolModuleStateSchema,
  ims_a_inventory:           ImsAInventorySchema,
  ims_b_inventory:           ImsBInventorySchema,
  ims_c_inventory:           ImsCInventorySchema,
  ims_d_inventory:           ImsDInventorySchema,
  ims_locations:             ImsLocationSchema,
  ims_lot_records:           ImsLotRecordSchema,
  kiol_forecasts:            KiolForecastSchema,
  demand_sensing_signals:    DemandSensingSignalSchema,
  kiol_demand_signals:       KiolDemandSignalSchema,
  meio_policies:             MeioPolicySchema,
  meio_runs:                 MeioRunSchema,
  meio_optimization_results: MeioOptimizationResultSchema,
  supplier_inventory_feeds:  SupplierInventoryFeedSchema,
  supplier_risk_signals:     SupplierRiskSignalSchema,
  supplier_tier_graph:       SupplierTierGraphSchema,
  po_recommendations:        PoRecommendationSchema,
  kiol_pooling_agreements:   KiolPoolingAgreementSchema,
  kiol_events:               KiolEventSchema,
  supply_schema_versions:    SupplySchemaVersionRecordSchema,
  supply_migrations:         SupplyMigrationSchema,
});
